import _axios from 'axios';
import axiosRetry from 'axios-retry';
import bus from '@/utils/bus';
import store from '@/store';
import enums from '@/utils/enums';
import { APP, CONFIG } from '@/utils/constants';
import logTrace from '@/services/logTrace';
import { getToken } from '@/services/auth';

const axios = _axios.create();
axiosRetry(axios, {
  retries: 1,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: axiosRetry.isRetryableError
});

async function requestService(
  url,
  method = 'GET',
  {
    data = null,
    params = null,
    localErrorHandling = false,
    auth = true,
    customHeaders = null,
    useLoading = true,
    sendTenant = true,
    retries = null,
    overwriteOptions = {}
  } = {}
) {
  const requestParams = {
    url,
    method,
    data,
    params,
    ...overwriteOptions
  };
  try {
    const defaultHeaders = {};
    if (auth) defaultHeaders['Authorization'] = getToken();
    if (sendTenant)
      defaultHeaders['Tenant'] = store.getters[CONFIG.GET].tenantId;
    if (retries != null && retries >= 0){
      requestParams['axios-retry'] = {
        retries
      }
    }
    if (useLoading) store.commit(APP.LOADING);
    requestParams.headers = customHeaders || defaultHeaders;
    const call = await axios({
      ...requestParams,
      paramsSerializer
    });
    if (call.status === 401) {
      bus.$emit('unauthorized');
    }
    return call.data;
  } catch (error) {
    // ? Send logtrace error
    const lastSW = sessionStorage.getItem('lastSW')
      ? new Date(
          Number.parseInt(sessionStorage.getItem('lastSW'))
        ).toISOString()
      : undefined;
    let traceInfo = {
      message: error.message,
      request: requestParams,
      stack: error.stack,
      userAgent: navigator.userAgent,
      lastSW,
      url: window.location.toString(),
      isOnline: window.navigator.onLine
    };
    let isOffline = false;
    let logtraceAction = enums.logTraceActions.bexsServerError;
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.data && getToken()) {
        traceInfo.status = error.response.status;
        if (typeof error.response.data === 'string') {
          traceInfo.error = error.response.data;
        } else {
          traceInfo = {
            ...traceInfo,
            ...error.response.data
          };
        }
      }

      if (!localErrorHandling) {
        if (error.response.status === 401) {
          bus.$emit('unauthorized');
        }
        bus.$emit('snackbar:open', {
          text: enums.responses.error,
          color: 'red'
        });
      }
      logTrace({
        action: logtraceAction,
        details: traceInfo
      });
      throw {
        ...error.response,
        stack: error.stack
      };
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      isOffline = true;
      bus.$emit('snackbar:open', {
        text: enums.responses.connectionError,
        color: 'orange'
      });
      logtraceAction = enums.logTraceActions.connectionError;
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }
    logTrace(
      {
        action: logtraceAction,
        details: traceInfo
      },
      isOffline
    );
    throw error;
  } finally {
    if (useLoading) store.commit(APP.LOADED);
  }
}
export default requestService;

const paramsSerializer = params => {
  var parts = [];

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      let val = params[key];
      if (val === null || typeof val === 'undefined') {
        continue;
      }
      if (Array.isArray(val)) {
        const arrayQuery = val.map(el => `${key}=${el}`).join('&');
        parts.push(arrayQuery);
      } else {
        if (typeof val === 'object') {
          val = JSON.stringify(val);
        }
        parts.push(encode(key) + '=' + encode(val));
      }
    }
  }

  return parts.join('&');
};

function encode(val) {
  return encodeURIComponent(val)
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+')
    .replace(/%5B/gi, '[')
    .replace(/%5D/gi, ']');
}



// WEBPACK FOOTER //
// ./src/services/request.js